import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  ErrorLike,
  LocalStateError,
  ServerError,
  ServerParseError,
  UnconventionalError,
} from '@apollo/client'

import * as Sentry from '@sentry/react'
import isTestEnv from 'utils/isTestEnv'

// `graphql_rails` tags every error it renders with a type.
//
// `validation_error` is business logic, not a fault. `system_error` is an
// unhandled backend exception, which `ApplicationGraphqlController` has already
// sent to the backend Sentry project
const UNREPORTED_ERROR_TYPES: Array<string | undefined> = [
  'system_error',
  'validation_error',
]

export function logException(
  error: Error | ErrorLike | string,
  options: { extras?: Record<string, unknown> } = {}
) {
  const registerError = (
    error: CombinedGraphQLErrors | Error | string,
    extras?: Record<string, unknown>
  ) => {
    Sentry.withScope((scope) => {
      Object.entries({ ...options.extras, ...extras }).forEach(
        ([key, value]) => {
          scope.setExtra(key, value)
        }
      )

      Sentry.captureException(error)
    })

    if (!isTestEnv) console.error(error)
  }

  if (CombinedGraphQLErrors.is(error)) {
    // Handle GraphQL errors
    const errors = error.errors.filter((error) => {
      // `type` is a custom BE field, absent from GraphQLFormattedError
      const { type } = error as { type?: string }

      return (
        !UNREPORTED_ERROR_TYPES.includes(type) &&
        // Workaround for incorrect BE type
        // Message _can_ sometimes be undefined in GraphQLFormattedError
        !error.message.includes('Cannot return null for non-nullable field')
      )
    })

    // Pass `error` itself: spreading strips the Error prototype, and Sentry
    // then falls back to grouping by minified function name.
    if (errors.length) registerError(error, { reportedErrors: errors })
  } else if (CombinedProtocolErrors.is(error)) {
    // Handle multipart subscription protocol errors
    registerError(error)
  } else if (LocalStateError.is(error)) {
    // Handle errors thrown by the `LocalState` class
    registerError(error)
  } else if (ServerError.is(error)) {
    // Handle server HTTP errors
    // SKIP
  } else if (ServerParseError.is(error)) {
    // Handle JSON parse errors
    registerError(error)
  } else if (UnconventionalError.is(error)) {
    // Handle errors thrown by irregular types
    registerError(error)
  } else {
    // Handle other errors
    registerError(error)
  }
}
